import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { PLATFORM_META, PlatformBadge, inr } from "../platforms";
import { Plus, Trash2, Pencil, FileText, Truck, Sparkles, Loader2, UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";
const emptyItem = { sku_code: "", description: "", hsn: "", upc: "", qty: 1, unit: "SET", rate: 0, discount_pct: 0, gst_rate: 18, cess_rate: 0 };
const emptyForm = { platform: "amazon", expected_date: "", bill_to_id: "", ship_to_id: "", platform_fields: {}, transport: {}, items: [{ ...emptyItem }], notes: "" };

export default function Shipments() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [parties, setParties] = useState([]);
  const [skus, setSkus] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/shipments").then((r) => setShipments(r.data));
  useEffect(() => {
    load();
    api.get("/parties").then((r) => setParties(r.data));
    api.get("/skus").then((r) => setSkus(r.data));
    api.get("/platforms").then((r) => setConfig(r.data));
  }, []);

  useEffect(() => {
    if (form.platform) api.get("/sku-mappings", { params: { platform: form.platform } }).then((r) => setMappings(r.data));
  }, [form.platform]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setItem = (i, patch) => set({ items: form.items.map((it, x) => (x === i ? { ...it, ...patch } : it)) });
  const pickSku = (i, code) => {
    const sku = skus.find((s) => s.sku_code === code);
    if (sku) setItem(i, { sku_code: sku.sku_code, description: sku.name, hsn: sku.hsn, gst_rate: sku.gst_rate, unit: sku.unit, rate: sku.price, upc: sku.upc || "" });
    else setItem(i, { sku_code: code });
  };

  const uploadAI = async (file) => {
    if (!file) return;
    setAiBusy(true);
    const fd = new FormData();
    fd.append("platform", form.platform);
    fd.append("file", file);
    try {
      const { data: job } = await api.post("/ai/extract", fd, { timeout: 60000 });
      let data = null;
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: st } = await api.get(`/ai/extract/jobs/${job.job_id}`);
        if (st.status === "done") { data = st.result; break; }
        if (st.status === "error") throw new Error(st.error || "AI extraction failed");
      }
      if (!data) throw new Error("AI extraction timed out — try a smaller file");
      const patch = {};
      if (data.platform_fields) patch.platform_fields = { ...form.platform_fields, ...Object.fromEntries(Object.entries(data.platform_fields).filter(([, v]) => v)) };
      if (data.transport) patch.transport = { ...form.transport, ...Object.fromEntries(Object.entries(data.transport).filter(([, v]) => v)) };
      if (data.items?.length) {
        patch.items = data.items.map((it) => ({
          ...emptyItem, sku_code: it.sku_code || "", description: it.description || "", hsn: String(it.hsn || ""),
          upc: String(it.upc || ""), qty: Number(it.qty) || 1, unit: it.unit || "SET",
          rate: Number(it.rate) || 0, gst_rate: Number(it.gst_rate) || 18,
        }));
      }
      set(patch);
      toast.success(`AI extracted ${data.items?.length || 0} items`);
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, items: form.items.map((it) => ({ ...it, qty: Number(it.qty), rate: Number(it.rate), gst_rate: Number(it.gst_rate), discount_pct: Number(it.discount_pct) || 0, cess_rate: Number(it.cess_rate) || 0 })) };
      if (editId) await api.put(`/shipments/${editId}`, payload);
      else await api.post("/shipments", payload);
      toast.success("Shipment saved");
      setOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const createInvoice = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/shipments/${id}/create-invoice`);
      toast.success(`Invoice ${data.invoice_no} created from shipment`);
      navigate(`/invoices/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const pconf = config?.platforms?.[form.platform];

  return (
    <div data-testid="shipments-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Planning</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Shipment Book</h1>
          <p className="text-sm text-slate-500 mt-1">Plan future shipments in advance, then create invoices in one click.</p>
        </div>
        <button data-testid="add-shipment-btn" onClick={() => { setForm({ ...emptyForm, expected_date: new Date().toISOString().slice(0, 10) }); setEditId(null); setOpen(true); }}
          className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">
          <Plus size={16} /> Plan Shipment
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shipments.map((sh) => (
          <div key={sh.id} data-testid={`shipment-card-${sh.id}`} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow duration-200 flex flex-col gap-3">
            <div className="flex items-center gap-2.5">
              <PlatformBadge platform={sh.platform} size={15} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{PLATFORM_META[sh.platform]?.name} · {sh.expected_date}</div>
                <div className="text-xs text-slate-500 truncate">{parties.find((p) => p.id === sh.ship_to_id)?.name || "—"}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 font-semibold border ${sh.status === "invoiced" ? "bg-blue-50 text-blue-800 border-blue-300" : "bg-emerald-50 text-emerald-800 border-emerald-300"}`}>
                {sh.status}
              </span>
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-4">
              <span>{sh.items?.length || 0} item(s)</span>
              <span>Qty {sh.items?.reduce((a, i) => a + Number(i.qty || 0), 0)}</span>
              <span className="font-mono">{inr(sh.items?.reduce((a, i) => a + Number(i.qty || 0) * Number(i.rate || 0), 0))} + tax</span>
            </div>
            {Object.values(sh.platform_fields || {}).some(Boolean) && (
              <div className="text-xs font-mono text-slate-600 truncate">
                {Object.entries(sh.platform_fields).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" | ")}
              </div>
            )}
            <div className="flex gap-2 mt-auto pt-1">
              {sh.status === "planned" ? (
                <>
                  <button data-testid={`shipment-create-invoice-${sh.id}`} onClick={() => createInvoice(sh.id)} disabled={busy === sh.id}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#0A192F] hover:bg-[#12233f] text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors duration-200 disabled:opacity-60">
                    {busy === sh.id ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Create Invoice
                  </button>
                  <button data-testid={`shipment-edit-${sh.id}`} onClick={() => { setForm({ ...emptyForm, ...sh, items: sh.items?.length ? sh.items : [{ ...emptyItem }] }); setEditId(sh.id); setOpen(true); }}
                    className="p-2 text-slate-400 hover:text-[#0047AB] transition-colors duration-200"><Pencil size={15} /></button>
                  <button data-testid={`shipment-delete-${sh.id}`} onClick={async () => { await api.delete(`/shipments/${sh.id}`); load(); }}
                    className="p-2 text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={15} /></button>
                </>
              ) : (
                <button onClick={() => navigate(`/invoices/${sh.invoice_id}`)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-semibold px-3 py-2 rounded-full transition-colors duration-200">
                  <FileText size={13} /> View Invoice
                </button>
              )}
            </div>
          </div>
        ))}
        {shipments.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400 text-sm">
            <Truck size={28} className="mx-auto mb-2 text-slate-300" />
            No planned shipments. Click "Plan Shipment" to add your upcoming dispatches.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm); setEditId(null); } }}>
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Shipment" : "Plan Shipment"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Platform *</label>
                <select data-testid="shipment-platform-select" value={form.platform} onChange={(e) => set({ platform: e.target.value, platform_fields: {} })} className={`${inputCls} mt-1`}>
                  {Object.entries(PLATFORM_META).map(([k, m]) => <option key={k} value={k}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Expected Dispatch Date *</label>
                <input data-testid="shipment-date-input" type="date" required value={form.expected_date} onChange={(e) => set({ expected_date: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className={labelCls}>Bill To *</label>
                <select data-testid="shipment-billto-select" required value={form.bill_to_id} onChange={(e) => set({ bill_to_id: e.target.value })} className={`${inputCls} mt-1`}>
                  <option value="">Select...</option>
                  {parties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_own_branch ? " (Own Branch)" : ""}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Ship To *</label>
                <select data-testid="shipment-shipto-select" required value={form.ship_to_id} onChange={(e) => set({ ship_to_id: e.target.value })} className={`${inputCls} mt-1`}>
                  <option value="">Select...</option>
                  {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {pconf?.fields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}</label>
                  <input data-testid={`shipment-pf-${f.key}-input`} value={form.platform_fields[f.key] || ""}
                    onChange={(e) => set({ platform_fields: { ...form.platform_fields, [f.key]: e.target.value } })} className={`${inputCls} mt-1 font-mono`} />
                </div>
              ))}
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 flex items-center gap-3">
              <Sparkles size={18} className="text-[#0047AB] shrink-0" />
              <span className="text-xs text-slate-500 flex-1">Upload PO / RO (PDF or JPG) — AI fills items &amp; details</span>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="shipment-ai-file-input" onChange={(e) => uploadAI(e.target.files[0])} />
              <button type="button" data-testid="shipment-ai-upload-btn" disabled={aiBusy} onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 bg-[#0047AB] hover:bg-[#003380] text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors duration-200 disabled:opacity-60">
                {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />} {aiBusy ? "Reading..." : "Upload"}
              </button>
            </div>

            <div className="space-y-2">
              <label className={labelCls}>Items</label>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input list="ship-sku-list" placeholder="SKU" data-testid={`shipment-item-${i}-sku`} value={it.sku_code} onChange={(e) => pickSku(i, e.target.value)} className={`${inputCls} col-span-3 font-mono text-xs`} />
                  <input placeholder="Description" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} className={`${inputCls} col-span-4`} />
                  <input placeholder="Qty" type="number" data-testid={`shipment-item-${i}-qty`} value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className={`${inputCls} col-span-2 text-right`} />
                  <input placeholder="Rate" type="number" step="0.01" data-testid={`shipment-item-${i}-rate`} value={it.rate} onChange={(e) => setItem(i, { rate: e.target.value })} className={`${inputCls} col-span-2 text-right`} />
                  <button type="button" onClick={() => set({ items: form.items.filter((_, x) => x !== i) })} className="col-span-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              ))}
              <datalist id="ship-sku-list">
                {skus.map((s) => <option key={s.id} value={s.sku_code} label={s.name} />)}
                {mappings.map((m) => <option key={m.id} value={m.platform_sku} label={`→ ${m.master_sku}`} />)}
              </datalist>
              <button type="button" data-testid="shipment-add-item-btn" onClick={() => set({ items: [...form.items, { ...emptyItem }] })}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#0047AB] hover:underline"><Plus size={13} /> Add item</button>
            </div>

            <div>
              <label className={labelCls}>Notes</label>
              <textarea data-testid="shipment-notes-input" value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} className={`${inputCls} mt-1`} />
            </div>
            <div className="flex justify-end">
              <button data-testid="shipment-save-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors duration-200">Save Shipment</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
